import { Injectable } from '@angular/core';
import type Rollbar from 'rollbar';
import { Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CoreModule } from '../core.module';
import { AuthState } from '../states/auth.state';
import { OrganisationsState } from '../states/organisations.state';
import { VERSION } from 'src/app/core/version';
@Injectable({
  providedIn: CoreModule,
})
export class RollbarService {
  public settings: Rollbar.Configuration = {
    accessToken: environment.rollbar.token,
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: environment.name,
    enabled: environment.rollbar.enable,
    itemsPerMinute: 5,
    captureIp: 'anonymize' as any,
    code_version: VERSION,
    payload: {
      code_version: VERSION,
      codeVersion: VERSION,
    },
  };

  // Rollbar is ~100 KB, so it's only loaded once there's an error to report or a user to tag:
  // anonymous visitors of the public pages never download it. Uncaught errors still get here,
  // provideBrowserGlobalErrorListeners() routes them through GenericErrorHandler.
  private rollbar?: Promise<Rollbar>;

  constructor(private store: Store) {}

  private load() {
    return (this.rollbar ??= import('rollbar').then(({ default: Rollbar }) => new Rollbar(this.settings)));
  }

  public report(level: 'error' | 'warning', ...args: Rollbar.LogArgument[]) {
    this.load().then((rollbar) => rollbar[level](...args));
  }

  // Called from AppComponent.ngOnInit(), not from this constructor: RollbarService is
  // constructed as a dependency of GenericErrorHandler, which the app creates before NGXS
  // has finished registering its root state, so selecting from the store here would throw.
  public trackUser() {
    // Keep every Rollbar item (including auto-captured uncaught errors) tagged with
    // the logged in user and their organisation, so items can be traced back without
    // having to attach this data at every individual call site.
    combineLatest([
      this.store.select(AuthState.currentUser),
      this.store.select(OrganisationsState.myOrganisation),
    ]).subscribe(([user, organisation]) => {
      if (!user) {
        // Explicitly null every field instead of omitting them: Rollbar's configure()
        // deep-merges options, so omitted (undefined) fields would leave the previous
        // user's data attached to error reports sent while logged out.
        this.rollbar?.then((rollbar) =>
          rollbar.configure({
            payload: {
              person: {
                id: null,
                email: null,
                username: null,
                organisationId: null,
                organisationName: null,
                roles: null,
              },
              custom: { userId: null, organisationId: null, organisationName: null, roles: null },
            },
          } as any)
        );
        return;
      }

      this.load().then((rollbar) =>
        rollbar.configure({
          payload: {
            person: {
              id: user.sub,
              email: user.email,
              username: user.email,
              organisationId: user.organisationId,
              organisationName: organisation?.name,
              roles: user.roles,
            },
            custom: {
              userId: user.sub,
              organisationId: user.organisationId,
              organisationName: organisation?.name,
              roles: user.roles,
            },
          },
        })
      );
    });
  }
}
